import { find } from 'lodash'
import { Grid, Paper, Hidden } from 'material-ui'
import Fade from 'material-ui/transitions/Fade'
import Typography from 'material-ui/Typography'
import * as React from 'react'
import { CSSProperties, SFC } from 'react'
import { BigGreenButton } from '../components/BigGreenButton'
import { SpreadBody } from '../components/SpreadBody'
import { SpreadHeading } from '../components/SpreadHeading'

interface Capacity {
    id: string
    icon: string
    title: string
    subtitle: string
    body: string
}

interface State {
    selectedCapacityId?: string
}

export class AiSpread extends React.Component<{}, State> {

    state: State = {}

    capacities: Capacity[] = [
        {
            id: 'strategy',
            icon: '/img/service-icons/discovery.svg',
            title: 'Business Strategy',
            subtitle: 'Multiply your AI Impact By Starting with Clear Strategy',
            body: 'Business Strategy + AI lets you understand the potential of your data, ' +
            'and understand how AI can help with your business needs. Though AI is not magic nor a cure-all, ' +
            'if you know the specifics of how to leverage it, you can build the right plan. '
        },
        {
            id: 'ui',
            icon: '/img/service-icons/ui.svg',
            title: 'UI/UX Design',
            subtitle: 'AI Should be Shaped by the Users',
            body: 'AI shouldn\'t be designed and built in a vacuum. The necessary accuracy, the types of models, ' +
            'the hyperparameters should be affected by the needs of the users and how that AI model is ' +
            'incorporated into the final product or service. '
        },
        {
            id: 'apps',
            icon: '/img/service-icons/app.svg',
            title: 'Productionalize',
            subtitle: 'Getting your AI into Production',
            body: 'Whether "Productionalize" is a word or not, it means taking a trained, ' +
            'accurate AI model and incorporating it into a production environment. This could include plugging ' +
            'into streams of data, routinely re-training, and needing fast prediction speeds. '
        }
    ]

    AiBox = () => (
        <Paper
            elevation={16}
            style={{
                textAlign: 'center',
                height: 300,
                width: 300,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                float: 'left',
                margin: 0
            }}
        >
            <div style={{height: 180, width: 200}}>
                <Typography
                    type='display4'
                    style={{lineHeight: '1em', fontSize: '9em'}}
                >
                    AI
                </Typography>
                <Typography type='subheading'>Artificial Intelligence</Typography>
            </div>
        </Paper>
    )

    BigPlus = () => (
        <Typography
            type='display4'
            style={{
                fontSize: '9em',
                lineHeight: '2em',
                textAlign: 'center',
                float: 'left',
                margin: '15px auto',
                width: 200
            }}
        >
            +
        </Typography>
    )

    SelectionBox: SFC<{ selectedCapacity?: Capacity }> = ({selectedCapacity}) => {

        if (selectedCapacity) {
            return (
                <Paper
                    elevation={16}
                    style={{
                        textAlign: 'center',
                        height: 300,
                        width: 300,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        float: 'left',
                        margin: 0
                    }}
                >
                    <div style={{height: 180, width: 200}}>
                        <div style={{height: 150}}>
                            <img
                                src={selectedCapacity.icon}
                                alt={selectedCapacity.title}
                                style={{
                                    margin: 25, height: 100
                                }}
                            />
                        </div>
                        <Typography type='subheading'>{selectedCapacity.title}</Typography>
                    </div>
                </Paper>
            )
        } else {
            const {EmptySelectionBox} = this
            return (
                <Fade in={true}>
                    <EmptySelectionBox/>
                </Fade>
            )
        }
    }

    EmptySelectionBox = () => (

        <Typography
            style={{
                textAlign: 'center',
                height: 196,
                width: 196,
                float: 'left',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '2px dashed #CCC',
                lineHeight: '2em',
                padding: 50,
                margin: 0
            }}
            type='subheading'
        >
            Select a Capacity from the boxes below to see how it enhances AI
        </Typography>
    )

    AiHeading = () => (
        <article style={{textAlign: 'left', margin: '0 20px 0'}}>
            <SpreadHeading title='Not Just AI. AI+'/>
            <Typography type='subheading' style={{lineHeight: '2em', maxWidth: 430, marginTop: 15}}>
                Artificial Intelligence is all the rage these days, but we see some common failures. AI should be part
                of an integrated solution and here's how our core capacities enhance AI.
            </Typography>
        </article>
    )

    CapacitySelector: SFC<{
        capacity: Capacity
        selected: boolean
        index: number
    }> = ({capacity, selected, index}) => {
        const paperStyle: CSSProperties = {
            minHeight: 200,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'box-shadow .3s',
            cursor: 'pointer',
            margin: '20px 20px 0'
        }

        return (
            <Fade in={!this.state.selectedCapacityId} timeout={{enter: (index + 1) * 400, exit: 0}} unmountOnExit>
                <Grid item xs={12} sm={4}>
                    <Paper
                        elevation={selected ? 24 : 8}
                        style={paperStyle}
                        onClick={() => this.setState({selectedCapacityId: capacity.id})}
                    >
                        <div style={{textAlign: 'center'}}>
                            <img src={capacity.icon} alt={capacity.title} style={{margin: 10}}/>
                            <Typography type='body1'>{capacity.title}</Typography>
                        </div>
                    </Paper>
                </Grid>
            </Fade>
        )
    }

    CapacitySelectors = () => {
        const {CapacitySelector, capacities} = this
        const {selectedCapacityId} = this.state

        return (
            <Grid container spacing={0} style={{paddingBottom: 0}}>
                {capacities.map((c, i) =>
                    <CapacitySelector key={i} index={i} capacity={c} selected={c.id === selectedCapacityId}/>
                )}
            </Grid>
        )
    }

    SelectedCapacity = ({selectedCapacity}: { selectedCapacity: Capacity }) => (
        <div style={{margin: '100px auto 20px', width: '90%', maxWidth: 600, textAlign: 'center'}}>
            <Typography type='title' style={{margin: 20}}>{selectedCapacity.subtitle}</Typography>
            <Typography type='subheading' style={{lineHeight: '2em'}}>{selectedCapacity.body}</Typography>
            <BigGreenButton text='Reset' onClick={this.resetSelected}/>
        </div>
    )

    resetSelected = () => this.setState({selectedCapacityId: undefined})

    render() {

        const {selectedCapacityId} = this.state

        const selectedCapacity = (selectedCapacityId)
            ? find(this.capacities, c => c.id === selectedCapacityId)
            : undefined

        const {AiHeading, SelectionBox, AiBox, BigPlus, CapacitySelectors, SelectedCapacity} = this

        return (
            <Hidden smDown>
                <SpreadBody greyBg>
                    <AiHeading/>
                    <div style={{width: 800, minHeight: 300, margin: '50px auto'}}>
                        <AiBox/>
                        <BigPlus/>
                        <SelectionBox selectedCapacity={selectedCapacity}/>
                    </div>
                    <div style={{width: 800, margin: '25px auto 0'}}>
                        <Fade in={!!selectedCapacity} timeout={{enter: 600, exit: 0}} unmountOnExit>
                            <div>
                                {selectedCapacity && <SelectedCapacity selectedCapacity={selectedCapacity}/>}
                            </div>
                        </Fade>
                        <CapacitySelectors/>
                    </div>
                </SpreadBody>
            </Hidden>
        )
    }
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/views/index/AiSpread.tsx